import { ArrowRight, Check, ContactRound, FileText, MessageSquareText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, buttonVariants } from '@/components/ui';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

type FeatureKind = 'cv' | 'linkedin' | 'interview';
const featureIcon = { cv: FileText, linkedin: ContactRound, interview: MessageSquareText };

export function FeaturePage({ kind }: { kind: FeatureKind }) {
  const { t } = useTranslation(['marketing', 'common']);
  const points = t(`marketing:features.${kind}.points`, { returnObjects: true }) as string[];
  const Icon = featureIcon[kind];
  const title = t(`marketing:features.${kind}.title`);
  const description = t(`marketing:features.${kind}.description`);
  usePageMeta({ title, description });

  return (
    <div className={`feature-detail feature-detail--${kind}`}>
      <section className="section-pad feature-detail__hero">
        <div className="container feature-detail__hero-grid">
          <div>
            <Badge tone="brand">
              <Icon size={14} /> {t('marketing:features.shared.eyebrow')}
            </Badge>
            <h1>{title}</h1>
            <p>{description}</p>
            <div className="hero-actions">
              <Link className={buttonVariants({ size: 'lg' })} to={routes.register}>
                {t('common:actions.startFree')} <ArrowRight className="rtl-flip" size={18} />
              </Link>
              <Link
                className={buttonVariants({ variant: 'secondary', size: 'lg' })}
                to={routes.pricing}
              >
                {t('common:nav.pricing')}
              </Link>
            </div>
          </div>
          <FeaturePreview kind={kind} />
        </div>
      </section>
      <section className="section-pad">
        <div className="container feature-benefits">
          <div>
            <p className="eyebrow">{t(`marketing:features.${kind}.previewTitle`)}</p>
            <h2>{t(`marketing:features.${kind}.previewBody`)}</h2>
          </div>
          <ul>
            {points.map((point) => (
              <li key={point}>
                <span>
                  <Check size={17} />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="section-pad feature-detail__cta">
        <div className="container final-cta">
          <div>
            <h2>{t('marketing:features.shared.ctaTitle')}</h2>
            <p>{t('marketing:features.shared.ctaBody')}</p>
          </div>
          <Link
            className={buttonVariants({ variant: 'secondary', size: 'lg' })}
            to={routes.register}
          >
            {t('common:actions.startFree')}
          </Link>
        </div>
      </section>
    </div>
  );
}

function FeaturePreview({ kind }: { kind: FeatureKind }) {
  const rows =
    kind === 'interview'
      ? ['HR / General', 'Role specific', 'CV-specific']
      : kind === 'linkedin'
        ? ['Headline', 'About', 'Experience']
        : ['Experience', 'Education', 'Skills'];
  return (
    <div className="feature-preview" aria-hidden="true">
      <div className="feature-preview__bar">
        <span />
        <span />
        <span />
      </div>
      <div className="feature-preview__body">
        <div className="feature-preview__score">
          <span>{kind === 'linkedin' ? '64' : kind === 'interview' ? '72' : '82'}</span>
          <small>/ 100</small>
        </div>
        <div className="feature-preview__rows">
          {rows.map((row, index) => (
            <div key={row}>
              <span>{row}</span>
              <i style={{ inlineSize: `${82 - index * 14}%` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
